import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  CheckCircle as ActiveIcon,
  Cancel as ExpiredIcon,
  PushPin as PinIcon,
  Schedule as ScheduledIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridPinnedRowsProp,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRowModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import * as LoyaltyService from '~/services/LoyaltyService';

import {
  CUSTOM_RULE_TYPE_OPTIONS,
  type CustomRule,
  type CustomRuleSearchParams,
  type CustomRuleSearchResponse,
  type CustomRuleStatusCounts,
  type CustomRuleType,
  DEFAULT_PAGE_SIZE,
  POINTS_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  type PageInfo,
  type PointsType,
  type ProductType,
} from '~/types/loyaltyContract';

// RowPinningSection is shared with manual accruals, so it stays in Loyalty/components.
import RowPinningSection from '../components/RowPinningSection';

import CreateCustomRuleDialog from './CreateCustomRuleDialog';
import ReadOnlyCustomRuleDialog from './ReadOnlyCustomRuleDialog';
import UpdateCustomRuleDialog from './UpdateCustomRuleDialog';
import { CellWithTooltip, HeaderWithTooltip, LabeledCellWithTooltip } from './components/CustomTooltip';
import PreferencesDrawer from './components/PreferencesDrawer';
import SearchFiltersSection from './components/SearchFiltersSection';

/**
 * localStorage key for saved column-visibility preferences. Bumped to V2 when the
 * default column set was trimmed so existing users pick up the new lean defaults once.
 */
const COLUMN_PREFERENCES_STORAGE_KEY = 'loyaltyCustomRulesColumnPreferencesV2';

const DEFAULT_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  id: false,
  name: true,
  type: true,
  purpose: false,
  pointsType: true,
  productType: false,
  offerId: false,
  packageId: false,
  earnKey: false,
  promoKey: false,
  region: false,
  pointsValue: true,
  pointsMultiplier: true,
  pointsRatio: false,
  pointsCap: false,
  startDate: true,
  endDate: false,
  status: true,
  pinAction: true,
};

const CustomToolbar: React.FC = () => {
  const theme = useTheme();

  return (
    <GridToolbarContainer
      sx={{
        padding: 2,
        gap: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(10px)',
      }}
    >
      <GridToolbarColumnsButton
        slotProps={{
          button: {
            sx: {
              borderRadius: 2,
              textTransform: 'none',
            },
          },
        }}
      />
      <GridToolbarFilterButton
        slotProps={{
          button: {
            sx: {
              borderRadius: 2,
              textTransform: 'none',
            },
          },
        }}
      />
      <GridToolbarExport
        slotProps={{
          button: {
            sx: {
              borderRadius: 2,
              textTransform: 'none',
            },
          },
        }}
      />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter
        debounceMs={500}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(10px)',
          },
        }}
      />
    </GridToolbarContainer>
  );
};

interface ColumnPreferencesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (selectedColumns: Record<string, boolean>) => void;
  availableColumns: Array<{ field: string; headerName: string }>;
}

const ColumnPreferencesModal: React.FC<ColumnPreferencesModalProps> = ({ open, onClose, onSave, availableColumns }) => {
  const theme = useTheme();
  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>(() => {
    const defaults: Record<string, boolean> = {};
    // Lean default set: the columns most useful at a glance. Everything else
    // stays available via the column picker.
    const essentialFields = ['name', 'type', 'pointsType', 'pointsValue', 'pointsMultiplier', 'startDate', 'status'];
    availableColumns.forEach((col) => {
      defaults[col.field] = essentialFields.includes(col.field);
    });
    return defaults;
  });

  // Load current preferences when modal opens
  useEffect(() => {
    if (open) {
      const savedPreferences = localStorage.getItem(COLUMN_PREFERENCES_STORAGE_KEY);
      if (savedPreferences) {
        try {
          setSelectedColumns(JSON.parse(savedPreferences));
        } catch (e) {
          console.error('Error parsing saved preferences in modal:', e);
        }
      }
    }
  }, [open]);

  const handleToggle = (field: string) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSelectAll = () => {
    const allSelected: Record<string, boolean> = {};
    availableColumns.forEach((col) => {
      allSelected[col.field] = true;
    });
    setSelectedColumns(allSelected);
  };

  const handleDeselectAll = () => {
    const noneSelected: Record<string, boolean> = {};
    availableColumns.forEach((col) => {
      noneSelected[col.field] = false;
    });
    setSelectedColumns(noneSelected);
  };

  const handleSave = () => {
    onSave(selectedColumns);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: theme.typography.fontWeightBold }}>
            Choose your columns
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select which columns you want to see by default. You can always change this later.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button size="small" variant="outlined" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1,
            }}
          >
            {availableColumns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox checked={selectedColumns[col.field] || false} onChange={() => handleToggle(col.field)} />
                }
                label={col.headerName}
              />
            ))}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="text">
          Skip for Now
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function LoyaltyCustomRulePage() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Sorting state. Undefined means "no explicit column sort": we omit sortBy/
  // sortOrder from the request so the server applies its default status-priority
  // grouping (Active → Scheduled → Expired). A column-header click sets these and
  // the server then sorts purely by that column.
  type SortBy = 'createdAt' | 'updatedAt' | 'startDate' | 'endDate' | 'name';
  type SortOrder = 'asc' | 'desc';
  const [sortBy, setSortBy] = useState<SortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(undefined);

  // Filter state
  const [filters, setFilters] = useState<CustomRuleSearchParams>({
    ids: [],
    offerIds: [],
    packageIds: [],
    ratePlanIds: [],
    regions: [],
    earnKeys: [],
    promoKeys: [],
    productTypes: [],
    types: [],
    pointsTypes: [],
  });

  // Form state
  const [customRuleIdInput, setCustomRuleIdInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [offerIdInput, setOfferIdInput] = useState('');
  const [packageIdInput, setPackageIdInput] = useState('');
  const [ratePlanIdInput, setRatePlanIdInput] = useState('');
  const [regionInput, setRegionInput] = useState('');
  const [earnKeyInput, setEarnKeyInput] = useState('');
  const [promoKeyInput, setPromoKeyInput] = useState('');
  const [selectedProductType, setSelectedProductType] = useState<ProductType | ''>('');
  const [selectedType, setSelectedType] = useState<CustomRuleType | ''>('');
  const [selectedPointsType, setSelectedPointsType] = useState<PointsType | ''>('');
  const [startDateFrom, setStartDateFrom] = useState('');
  const [endDateTo, setEndDateTo] = useState('');

  // Validation errors from API
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isReadOnlyDialogOpen, setIsReadOnlyDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<CustomRule | null>(null);

  // Status filter state
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'scheduled' | 'expired'>('all');

  // Load column visibility from localStorage or use defaults
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
    const savedPreferences = localStorage.getItem(COLUMN_PREFERENCES_STORAGE_KEY);
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        return {
          ...parsed,
          pinAction: true,
        };
      } catch (e) {
        console.error('Error parsing saved column preferences:', e);
      }
    }
    return DEFAULT_COLUMN_VISIBILITY;
  });

  // Row selection for pinning
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // Load pinned rows from localStorage
  const [pinnedRows, setPinnedRows] = useState<{
    top: Array<CustomRule>;
    bottom: Array<CustomRule>;
  }>(() => {
    const savedPinnedRows = localStorage.getItem('loyaltyCustomRulesPinnedRows');
    if (savedPinnedRows) {
      try {
        return JSON.parse(savedPinnedRows);
      } catch (e) {
        console.error('Error parsing saved pinned rows:', e);
      }
    }
    return {
      top: [],
      bottom: [],
    };
  });

  // Track newly created rules (with timestamp for auto-expiry)
  const [newlyCreatedRules, setNewlyCreatedRules] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setNewlyCreatedRules((prev) => {
        const updated = new Map(prev);
        let hasChanges = false;
        updated.forEach((timestamp, ruleId) => {
          if (now - timestamp > 30000) {
            // 30 seconds
            updated.delete(ruleId);
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const isInitialRenderRef = useRef(true);

  // Save column visibility to localStorage when it changes (user manually changes columns)
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    localStorage.setItem(COLUMN_PREFERENCES_STORAGE_KEY, JSON.stringify(columnVisibilityModel));
  }, [columnVisibilityModel]);

  // Save pinned rows to localStorage when they change
  useEffect(() => {
    localStorage.setItem('loyaltyCustomRulesPinnedRows', JSON.stringify(pinnedRows));
  }, [pinnedRows]);

  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [isPreferencesDrawerOpen, setIsPreferencesDrawerOpen] = useState(false);

  const [themeColor, setThemeColor] = useState<string>(() => {
    return localStorage.getItem('loyaltyCustomRulesThemeColor') || theme.palette.primary.main;
  });

  const handleThemeColorChange = useCallback((color: string) => {
    setThemeColor(color);
    localStorage.setItem('loyaltyCustomRulesThemeColor', color);
  }, []);

  const [showTooltips, setShowTooltips] = useState<boolean>(() => {
    const saved = localStorage.getItem('loyaltyCustomRulesTooltipsEnabled');
    return saved !== null ? saved === 'true' : true; // Default to true (enabled)
  });

  const handleTooltipToggle = useCallback((enabled: boolean) => {
    setShowTooltips(enabled);
    localStorage.setItem('loyaltyCustomRulesTooltipsEnabled', String(enabled));
  }, []);

  const { data, isLoading, error } = useQuery<CustomRuleSearchResponse>({
    queryKey: ['loyalty', 'custom-rules', filters, paginationModel, sortBy, sortOrder, statusFilter],
    queryFn: async ({ signal }) => {
      const params: CustomRuleSearchParams = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        status: statusFilter,
        // Only send sort params when the user explicitly sorted by a column.
        // Omitting them lets the server apply its default status-priority grouping.
        ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
        ...filters,
      };
      return await LoyaltyService.searchCustomRules(params, { signal });
    },
    placeholderData: (previousData) => previousData,
    enabled: true,
    retry: false,
  });

  const updateMutation = useMutation({
    mutationFn: (updateData: { id: string; data: Parameters<typeof LoyaltyService.updateCustomRule>[1] }) =>
      LoyaltyService.updateCustomRule(updateData.id, updateData.data),
    onSuccess: () => {
      enqueueSnackbar('Custom rule updated successfully', { variant: 'success' });
      // Invalidate queries to mark them as stale - they'll refetch automatically
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'custom-rules'] });
    },
    onError: (error: any) => {
      enqueueSnackbar(`Error updating custom rule: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });

  const customRules: Array<CustomRule> = data?.result?.customRules || [];
  const pageInfo: PageInfo | undefined = data?.result?.pageInfo;
  const totalCount: number = pageInfo?.pageCount ? pageInfo.pageCount * paginationModel.pageSize : 0;

  const unpinnedRows = useMemo(() => {
    const allPinnedIds = [...pinnedRows.top.map((r) => r.id), ...pinnedRows.bottom.map((r) => r.id)];

    return customRules.filter((rule) => !allPinnedIds.includes(rule.id));
  }, [customRules, pinnedRows]);

  const gridPinnedRows: GridPinnedRowsProp = useMemo(
    () => ({
      top: pinnedRows.top,
      bottom: pinnedRows.bottom,
    }),
    [pinnedRows],
  );

  const stats = useMemo((): CustomRuleStatusCounts => {
    return (
      data?.result?.counts || {
        total: 0,
        active: 0,
        scheduled: 0,
        expired: 0,
      }
    );
  }, [data]);

  useEffect(() => {
    if (error) {
      const apiError = error as { status?: number; errors?: Array<string>; message?: string };

      if (apiError?.status === 400 && apiError?.errors) {
        const errors: Record<string, string> = {};

        if (Array.isArray(apiError.errors)) {
          apiError.errors.forEach((errorMsg) => {
            if (typeof errorMsg === 'string') {
              const match = errorMsg.match(/^(\w+)(?:\.\d+)?:\s*(.+)$/);
              if (match) {
                const [, field, message] = match;
                errors[field] = message;
              }
            }
          });
        }

        setValidationErrors(errors);
        enqueueSnackbar(`Please fix validation errors`, { variant: 'error' });
      } else if (error instanceof Error) {
        enqueueSnackbar(`Error loading custom rules: ${error.message}`, { variant: 'error' });
      }
    }
  }, [error, enqueueSnackbar]);

  const handleSearch = useCallback((): void => {
    setValidationErrors({});
    const newFilters: CustomRuleSearchParams & { name?: string } = {
      ids: customRuleIdInput.trim() ? [customRuleIdInput.trim()] : [],
      name: nameInput.trim() || undefined,
      offerIds: offerIdInput.trim() ? [offerIdInput.trim()] : [],
      packageIds: packageIdInput.trim() ? [packageIdInput.trim()] : [],
      ratePlanIds: ratePlanIdInput.trim() ? [ratePlanIdInput.trim()] : [],
      regions: regionInput.trim() ? [regionInput.trim()] : [],
      earnKeys: earnKeyInput.trim() ? [earnKeyInput.trim()] : [],
      promoKeys: promoKeyInput.trim() ? [promoKeyInput.trim()] : [],
      productTypes: selectedProductType ? [selectedProductType] : [],
      types: selectedType ? [selectedType] : [],
      pointsTypes: selectedPointsType ? [selectedPointsType] : [],
      startDateFrom: startDateFrom
        ? dayjs.tz(startDateFrom, 'Australia/Sydney').startOf('day').toISOString()
        : undefined,
      endDateTo: endDateTo ? dayjs.tz(endDateTo, 'Australia/Sydney').endOf('day').toISOString() : undefined,
    };

    setFilters(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    customRuleIdInput,
    nameInput,
    offerIdInput,
    packageIdInput,
    ratePlanIdInput,
    regionInput,
    earnKeyInput,
    promoKeyInput,
    selectedProductType,
    selectedType,
    selectedPointsType,
    startDateFrom,
    endDateTo,
  ]);

  const handleClearFilters = useCallback((): void => {
    setCustomRuleIdInput('');
    setNameInput('');
    setOfferIdInput('');
    setPackageIdInput('');
    setRatePlanIdInput('');
    setRegionInput('');
    setEarnKeyInput('');
    setPromoKeyInput('');
    setSelectedProductType('');
    setSelectedType('');
    setSelectedPointsType('');
    setStartDateFrom('');
    setEndDateTo('');
    setSortBy(undefined);
    setSortOrder(undefined);
    setFilters({
      ids: [],
      offerIds: [],
      packageIds: [],
      ratePlanIds: [],
      regions: [],
      earnKeys: [],
      promoKeys: [],
      productTypes: [],
      types: [],
      pointsTypes: [],
    });
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  // Server-supported sort columns. `status` is special: it represents the default
  // status-priority grouping, which the server applies when no sortBy is sent — so
  // sorting by the status column clears the explicit sort rather than sending one.
  const STATUS_SORT_FIELD = 'status';

  // Current sort expressed as a DataGrid sort model. With no explicit sort we show
  // the status column as the active (descending) sort to signal the default grouping.
  const sortModel: GridSortModel =
    sortBy && sortOrder ? [{ field: sortBy, sort: sortOrder }] : [{ field: STATUS_SORT_FIELD, sort: 'desc' }];

  /**
   * Translates a DataGrid sort-model change into server sort params.
   * - name / createdAt -> sort by that column (server override).
   * - status (or no sort) -> clear sort so the server applies status grouping.
   * Resets to the first page so the user sees the start of the re-sorted results.
   */
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    const next = model[0];
    // Only name / createdAt are server-supported sort columns.
    if (next && (next.field === 'createdAt' || next.field === 'name')) {
      setSortBy(next.field);
      setSortOrder(next.sort === 'asc' ? 'asc' : 'desc');
    } else {
      // status column or sort cleared -> default grouping
      setSortBy(undefined);
      setSortOrder(undefined);
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRowClick = useCallback((params: GridRowParams<CustomRule>) => {
    const rule = params.row;
    setSelectedRule(rule);

    const hasStarted = rule.startDate && dayjs(rule.startDate).isBefore(dayjs());

    if (hasStarted) {
      // Rule has started - show readonly dialog
      setIsReadOnlyDialogOpen(true);
    } else {
      // Rule is scheduled - show update dialog
      setIsUpdateDialogOpen(true);
    }
  }, []);

  const handleUpdateDialogClose = useCallback((updatedRule?: CustomRule | null) => {
    // If the updated rule exists and is in pinned rows, update it there
    if (updatedRule) {
      setPinnedRows((prev) => {
        const updatedTop = prev.top.map((r) => (r.id === updatedRule.id ? updatedRule : r));
        const updatedBottom = prev.bottom.map((r) => (r.id === updatedRule.id ? updatedRule : r));
        return {
          top: updatedTop,
          bottom: updatedBottom,
        };
      });
    }
    setIsUpdateDialogOpen(false);
    setSelectedRule(null);
  }, []);

  const handleReadOnlyDialogClose = useCallback((updatedRule?: CustomRule | null) => {
    if (updatedRule) {
      setPinnedRows((prev) => {
        const updatedTop = prev.top.map((r) => (r.id === updatedRule.id ? updatedRule : r));
        const updatedBottom = prev.bottom.map((r) => (r.id === updatedRule.id ? updatedRule : r));
        return {
          top: updatedTop,
          bottom: updatedBottom,
        };
      });
    }
    setIsReadOnlyDialogOpen(false);
    setSelectedRule(null);
  }, []);

  const handleColumnVisibilityModelChange = useCallback((newModel: GridColumnVisibilityModel) => {
    // Always ensure the pin action column stays visible
    const updatedModel = {
      ...newModel,
      pinAction: true,
    };
    setColumnVisibilityModel(updatedModel);
  }, []);

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel<CustomRule>, oldRow: GridRowModel<CustomRule>): Promise<GridRowModel<CustomRule>> => {
      const hasChanges = Object.keys(newRow).some(
        (key) => newRow[key as keyof CustomRule] !== oldRow[key as keyof CustomRule],
      );

      if (!hasChanges) {
        return oldRow;
      }

      const hasRuleStarted = oldRow.startDate && dayjs(oldRow.startDate).isBefore(dayjs());

      try {
        if (hasRuleStarted) {
          if (newRow.endDate !== oldRow.endDate) {
            await updateMutation.mutateAsync({
              id: oldRow.id,
              data: {
                type: oldRow.type,
                endDate: newRow.endDate
                  ? dayjs.tz(newRow.endDate, 'Australia/Sydney').add(1, 'day').startOf('day').toISOString()
                  : null,
              },
            });
          } else {
            enqueueSnackbar('This rule has started. Only end date can be modified.', { variant: 'warning' });
            return oldRow;
          }
        } else {
          const updateData: Parameters<typeof LoyaltyService.updateCustomRule>[1] = {
            type: newRow.type,
            purpose: newRow.purpose,
            pointsType: newRow.pointsType,
            productType: newRow.productType || null,
            name: newRow.name,
            startDate: newRow.startDate
              ? dayjs.tz(newRow.startDate, 'Australia/Sydney').startOf('day').toISOString()
              : undefined,
            endDate: newRow.endDate
              ? dayjs.tz(newRow.endDate, 'Australia/Sydney').add(1, 'day').startOf('day').toISOString()
              : null,
            offerId: newRow.offerId || null,
            packageId: newRow.packageId || null,
            ratePlanId: newRow.ratePlanId || null,
            earnKey: newRow.earnKey || null,
            promoKey: newRow.promoKey || null,
            region: newRow.region || null,
            pointsValue: newRow.pointsValue ?? null,
            pointsMultiplier: newRow.pointsMultiplier ?? null,
            pointsCap: newRow.pointsCap ?? null,
            pointsRatio: newRow.pointsRatio ?? null,
          };

          await updateMutation.mutateAsync({
            id: oldRow.id,
            data: updateData,
          });
        }

        return newRow;
      } catch (error) {
        enqueueSnackbar('Failed to update row', { variant: 'error' });
        return oldRow;
      }
    },
    [updateMutation, enqueueSnackbar],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(`Error updating row: ${error.message}`, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const handlePinToTop = useCallback(() => {
    const selectedIds = rowSelectionModel as Array<string>;
    const selectedRules = customRules.filter((rule) => selectedIds.includes(rule.id));

    setPinnedRows((prev) => {
      const newBottom = prev.bottom.filter((row) => !selectedIds.includes(row.id));
      const existingTopIds = new Set(prev.top.map((r) => r.id));
      const newTop = [...prev.top, ...selectedRules.filter((r) => !existingTopIds.has(r.id))];

      return {
        top: newTop,
        bottom: newBottom,
      };
    });
    setRowSelectionModel([]);
    enqueueSnackbar(`${selectedIds.length} row(s) pinned to top`, { variant: 'success' });
    setTimeout(() => {
      if (apiRef.current) {
        apiRef.current.forceUpdate();
      }
    }, 0);
  }, [rowSelectionModel, customRules, enqueueSnackbar, apiRef]);

  const handlePinToBottom = useCallback(() => {
    const selectedIds = rowSelectionModel as Array<string>;
    const selectedRules = customRules.filter((rule) => selectedIds.includes(rule.id));

    setPinnedRows((prev) => {
      const newTop = prev.top.filter((row) => !selectedIds.includes(row.id));
      const existingBottomIds = new Set(prev.bottom.map((r) => r.id));
      const newBottom = [...prev.bottom, ...selectedRules.filter((r) => !existingBottomIds.has(r.id))];

      return {
        top: newTop,
        bottom: newBottom,
      };
    });
    setRowSelectionModel([]);
    enqueueSnackbar(`${selectedIds.length} row(s) pinned to bottom`, { variant: 'success' });
    setTimeout(() => {
      if (apiRef.current) {
        apiRef.current.forceUpdate();
      }
    }, 0);
  }, [rowSelectionModel, customRules, enqueueSnackbar, apiRef]);

  const handleUnpinAll = useCallback(() => {
    setPinnedRows({ top: [], bottom: [] });
    enqueueSnackbar('All rows unpinned', { variant: 'info' });
    setTimeout(() => {
      if (apiRef.current) {
        apiRef.current.forceUpdate();
      }
    }, 0);
  }, [enqueueSnackbar, apiRef]);

  const handleUnpinRow = useCallback(
    (rowId: string) => {
      setPinnedRows((prev) => ({
        top: prev.top.filter((row) => row.id !== rowId),
        bottom: prev.bottom.filter((row) => row.id !== rowId),
      }));
      setTimeout(() => {
        if (apiRef.current) {
          apiRef.current.forceUpdate();
        }
      }, 0);
    },
    [apiRef],
  );

  // Helper to check if a row is pinned
  const isRowPinned = useCallback(
    (rowId: string) => {
      return pinnedRows.top.some((r) => r.id === rowId) || pinnedRows.bottom.some((r) => r.id === rowId);
    },
    [pinnedRows],
  );

  const handleSaveColumnPreferences = useCallback(
    (selectedColumns: Record<string, boolean>) => {
      // Ensure the pin action column is always visible
      const updatedColumns = {
        ...selectedColumns,
        pinAction: true,
      };

      // Save to localStorage
      localStorage.setItem(COLUMN_PREFERENCES_STORAGE_KEY, JSON.stringify(updatedColumns));

      setColumnVisibilityModel(updatedColumns);

      setIsPreferencesModalOpen(false);

      enqueueSnackbar('Column preferences saved!', { variant: 'success' });
    },
    [enqueueSnackbar],
  );

  const getRuleTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      BONUS_POINTS: 'Bonus Points',
      MULTIPLE_ON_BASE_POINTS: 'Multiply Base',
      BONUS_POINTS_ONLY: 'Bonus Only',
    };
    return labels[type] || type;
  };

  const getRuleTypeTooltip = (type: string): string => {
    const tooltips: Record<string, string> = {
      BONUS_POINTS: 'Calculate and award fixed amount of bonus points on top of the base point when accrual happens',
      MULTIPLE_ON_BASE_POINTS:
        'Calculate and award bonus points by applying a multiplier to the base points when accrual occurs.(e.g., 2x, 3x)',
      BONUS_POINTS_ONLY: 'Calculate and award bonus points or status credits immediately, no base points given',
    };
    return tooltips[type] || type;
  };

  const getPurposeLabel = (purpose: string): string => {
    const labels: Record<string, string> = {
      ESTIMATION_ONLY: '',
      ESTIMATION_AND_EARN: 'Preview & Earn',
      ACCRUAL_OVERRIDE: 'Override Default',
    };
    return labels[purpose] || purpose;
  };

  const getPurposeTooltip = (purpose: string): string => {
    const tooltips: Record<string, string> = {
      ESTIMATION_ONLY: 'Award estimated points and award actual points calcualted in IBS when accrual happens',
      ESTIMATION_AND_EARN: 'Earn and award points directly to users',
      ACCRUAL_OVERRIDE: 'Override Default',
    };
    return tooltips[purpose] || purpose;
  };

  const getPointsTypeLabel = (pointsType: string): string => {
    const labels: Record<string, string> = {
      BONUS_POINTS: 'Bonus Points',
      BONUS_STATUS_CREDITS: 'Bonus Credits',
      BASE_POINTS: 'Base Points',
      STATUS_CREDITS: 'Status Credits',
    };
    return labels[pointsType] || pointsType;
  };

  const getPointsTypeTooltip = (pointsType: string): string => {
    const tooltips: Record<string, string> = {
      BONUS_POINTS: 'Extra points on top of base points',
      BONUS_STATUS_CREDITS: 'Extra credits toward tier status',
      BASE_POINTS: 'Standard points earned from purchase',
      STATUS_CREDITS: 'Credits that count toward tier status',
    };
    return tooltips[pointsType] || pointsType;
  };

  const COLUMN_WIDTH = 140;
  const COLUMN_WIDTH_NARROW = 100;
  const COLUMN_WIDTH_WIDE = 180;

  const columns: Array<GridColDef<CustomRule>> = useMemo(() => {
    // Inferred (not explicitly typed) to match the prior loose typing of this list;
    // an explicit GridColDef<CustomRule>[] annotation surfaces latent strict-mode
    // mismatches in pre-existing column literals unrelated to this change.
    const baseColumns = [
      {
        field: 'id',
        headerName: 'ID',
        width: COLUMN_WIDTH_WIDE,
        minWidth: COLUMN_WIDTH,
        editable: false,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip title="ID" tooltipText="Unique identifier for this custom rule" disabled={!showTooltips} />
        ),
        renderCell: ({ value }: GridRenderCellParams) => (
          <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH_WIDE - 20} disabled={!showTooltips} />
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        // Flex to absorb leftover horizontal space; still resizable by dragging.
        flex: 1,
        minWidth: 200,
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Name"
            tooltipText="Descriptive name for this custom rule"
            disabled={!showTooltips}
          />
        ),
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !params.props.value || params.props.value.trim() === '';
          return { ...params.props, error: hasError };
        },
        renderCell: ({ value, row }: GridRenderCellParams) => (
          <Box
            sx={{
              // Fill the actual column width (flexed or dragged) so widening the
              // column reveals more text instead of always truncating.
              width: '100%',
              height: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              gap: 0.5,
              overflow: 'hidden',
            }}
          >
            <Tooltip title={value || ''} placement="top" arrow>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'help',
                }}
              >
                {value || '—'}
              </Box>
            </Tooltip>
            {/* Only render the NEW badge slot when applicable so the name text
                gets the full column width the rest of the time. */}
            {newlyCreatedRules.has(row.id) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Chip
                  label="NEW"
                  size="small"
                  color="success"
                  sx={{
                    fontSize: theme.typography.caption.fontSize,
                    fontWeight: theme.typography.fontWeightBold,
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.6 },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        ),
      },
      {
        field: 'type',
        width: COLUMN_WIDTH_WIDE,
        minWidth: COLUMN_WIDTH,
        editable: true,
        filterable: true,
        type: 'singleSelect',
        valueOptions: CUSTOM_RULE_TYPE_OPTIONS,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Type"
            tooltipText="Defines how points are calculated and awarded. Hover over values for detailed explanation."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => (
          <LabeledCellWithTooltip
            value={value}
            getLabel={getRuleTypeLabel}
            getTooltip={getRuleTypeTooltip}
            maxWidth={COLUMN_WIDTH_WIDE}
            disabled={!showTooltips}
          />
        ),
      },
      {
        field: 'purpose',
        width: COLUMN_WIDTH_WIDE,
        minWidth: COLUMN_WIDTH_WIDE,
        editable: false,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Purpose"
            tooltipText="When the rule applies. Hover over values for detailed explanation."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => (
          <LabeledCellWithTooltip
            value={value}
            getLabel={getPurposeLabel}
            getTooltip={getPurposeTooltip}
            maxWidth={COLUMN_WIDTH_WIDE}
            disabled={!showTooltips}
          />
        ),
      },
      {
        field: 'pointsType',
        headerName: 'Points Type',
        width: COLUMN_WIDTH_WIDE,
        minWidth: COLUMN_WIDTH_WIDE,
        editable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Points Type"
            tooltipText="Type of points awarded. Hover over values for detailed explanation."
            disabled={!showTooltips}
          />
        ),
        filterable: true,
        type: 'singleSelect',
        valueOptions: POINTS_TYPE_OPTIONS.filter((type) => type === 'BONUS_POINTS' || type === 'BONUS_STATUS_CREDITS'),
        renderCell: ({ value }: GridRenderCellParams) => (
          <LabeledCellWithTooltip
            value={value}
            getLabel={getPointsTypeLabel}
            getTooltip={getPointsTypeTooltip}
            maxWidth={COLUMN_WIDTH_WIDE}
            disabled={!showTooltips}
          />
        ),
      },
      {
        field: 'pointsValue',
        headerName: 'Points Value',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        type: 'number',
        editable: true,
        filterable: true,
        headerAlign: 'left',
        align: 'center',
        renderHeader: () => (
          <HeaderWithTooltip
            title="Points Value"
            tooltipText="Fixed number of points to award. Used with BONUS_POINTS type."
            disabled={!showTooltips}
          />
        ),
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value != null && params.props.value < 0;
          return { ...params.props, error: hasError };
        },
        renderCell: ({ value }: GridRenderCellParams) => (
          <CellWithTooltip value={value?.toLocaleString()} maxWidth={COLUMN_WIDTH - 20} />
        ),
      },
      {
        field: 'pointsMultiplier',
        headerName: 'Multiplier',
        width: COLUMN_WIDTH_NARROW,
        minWidth: 80,
        type: 'number',
        editable: true,
        filterable: true,
        headerAlign: 'left',
        align: 'center',
        renderHeader: () => (
          <HeaderWithTooltip
            title="Multiplier"
            tooltipText="Multiply base points by this factor. Used with MULTIPLE_ON_BASE_POINTS type (e.g., 2x, 3x)."
            disabled={!showTooltips}
          />
        ),
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value != null && params.props.value < 0;
          return { ...params.props, error: hasError };
        },
        renderCell: ({ value }: GridRenderCellParams) => (
          <CellWithTooltip value={value != null ? `${value}x` : null} maxWidth={COLUMN_WIDTH_NARROW - 20} />
        ),
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: true,
        filterable: true,
        type: 'singleSelect',
        valueOptions: ['', ...PRODUCT_TYPE_OPTIONS],
        renderHeader: () => (
          <HeaderWithTooltip
            title="Product Type"
            tooltipText="Category of product this rule applies to (e.g., hotel_flash, cruise_group_1, tour_bank, flight types)"
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH - 20} />,
      },
      {
        field: 'earnKey',
        headerName: 'Earn Key',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Earn Key"
            tooltipText="Unique key for earning rules. Used to identify specific earning scenarios."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH - 20} />,
      },
      {
        field: 'promoKey',
        headerName: 'Promo Key',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Promo Key"
            tooltipText="Promotion code or key this rule applies to. Used for promotional campaigns."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH - 20} />,
      },
      {
        field: 'offerId',
        headerName: 'Offer ID',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Offer ID"
            tooltipText="Specific offer ID this rule applies to. Leave empty to apply to all offers."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH - 20} />,
      },
      {
        field: 'packageId',
        headerName: 'Package ID',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Package ID"
            tooltipText="Specific package ID this rule applies to. Leave empty to apply to all packages."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH - 20} />,
      },
      {
        field: 'ratePlanId',
        headerName: 'Rate Plan ID',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Rate Plan ID"
            tooltipText="Specific rate plan ID this rule applies to. Leave empty to apply to all rate plans."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH - 20} />,
      },
      {
        field: 'region',
        headerName: 'Region',
        width: COLUMN_WIDTH_NARROW,
        minWidth: 80,
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Region"
            tooltipText="Geographic region this rule applies to (e.g., AU, NZ, US, UK)"
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => (
          <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH_NARROW - 20} />
        ),
      },
      {
        field: 'pointsRatio',
        headerName: 'Ratio',
        width: COLUMN_WIDTH_NARROW,
        minWidth: 80,
        type: 'number',
        editable: true,
        filterable: true,
        align: 'right',
        headerAlign: 'right',
        renderHeader: () => (
          <HeaderWithTooltip
            title="Ratio"
            tooltipText="Points earned per dollar spent ratio (e.g., 1 means 1 point per $1)."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => (
          <CellWithTooltip value={value} maxWidth={COLUMN_WIDTH_NARROW - 20} />
        ),
      },
      {
        field: 'pointsCap',
        headerName: 'Cap',
        width: COLUMN_WIDTH_NARROW,
        minWidth: 80,
        type: 'number',
        editable: true,
        filterable: true,
        align: 'right',
        headerAlign: 'right',
        renderHeader: () => (
          <HeaderWithTooltip
            title="Cap"
            tooltipText="Maximum points that can be earned from this rule. Leave empty for no limit."
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ value }: GridRenderCellParams) => (
          <CellWithTooltip value={value?.toLocaleString()} maxWidth={COLUMN_WIDTH_NARROW - 20} />
        ),
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        type: 'date',
        editable: true,
        filterable: true,
        disableAutoSize: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Start Date"
            tooltipText="Date when this rule becomes active and starts applying to purchases."
            disabled={!showTooltips}
          />
        ),
        valueGetter: (value: string | null) => {
          if (!value) {
            return null;
          }
          return new Date(value);
        },
        valueFormatter: (value: Date | null) => {
          if (!value) {
            return '—';
          }
          return dayjs(value).format('DD/MM/YYYY');
        },
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const row = params.row as CustomRule;
          const hasStarted = row.startDate && dayjs(row.startDate).isBefore(dayjs());
          return { ...params.props, error: hasStarted };
        },
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        type: 'date',
        editable: true,
        filterable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            title="End Date"
            tooltipText="Date when this rule expires and stops applying. Leave empty for no expiration."
            disabled={!showTooltips}
          />
        ),
        valueGetter: (value: string | null) => {
          if (!value) {
            return null;
          }
          return dayjs(value).subtract(1, 'day').toDate();
        },
        valueFormatter: (value: Date | null) => {
          if (!value) {
            return '—';
          }
          return dayjs(value).format('DD/MM/YYYY');
        },
        valueSetter: (value: Date | null, row: CustomRule) => {
          return {
            ...row,
            endDate: value ? dayjs(value).format('YYYY-MM-DD') : null,
          };
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: false,
        filterable: false,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Created At"
            tooltipText="Date this rule was created (Sydney time)."
            disabled={!showTooltips}
          />
        ),
        // Date only, Sydney timezone.
        renderCell: ({ row }: GridRenderCellParams<CustomRule>) =>
          row.createdAt ? dayjs(row.createdAt).tz('Australia/Sydney').format('DD/MM/YYYY') : '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: COLUMN_WIDTH_NARROW,
        minWidth: COLUMN_WIDTH_NARROW,
        editable: false,
        filterable: false,
        sortable: false,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Status"
            tooltipText="Current status of the rule: Active (currently applying), Scheduled (future), or Expired (past end date)"
            disabled={!showTooltips}
          />
        ),
        renderCell: ({ row }: GridRenderCellParams<CustomRule>) => {
          const hasStarted = row.startDate && dayjs(row.startDate).isBefore(dayjs());
          const hasEnded = row.endDate && dayjs(row.endDate).isBefore(dayjs());

          if (hasEnded) {
            return (
              <Chip
                label="Expired"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.2),
                  color: theme.palette.grey[700],
                  fontWeight: theme.typography.fontWeightMedium,
                  fontSize: theme.typography.caption.fontSize,
                }}
              />
            );
          }

          if (hasStarted) {
            const endDate = row.endDate ? dayjs(row.endDate) : null;
            const now = dayjs();
            const daysUntilExpiration = endDate ? endDate.diff(now, 'day') : null;
            const hoursUntilExpiration = endDate ? endDate.diff(now, 'hour') : null;

            let expirationLabel = '';
            if (daysUntilExpiration !== null) {
              if (daysUntilExpiration === 0 && hoursUntilExpiration !== null) {
                // Less than a day: show hours
                if (hoursUntilExpiration <= 0) {
                  expirationLabel = '<1hr';
                } else if (hoursUntilExpiration === 1) {
                  expirationLabel = '1hr left';
                } else {
                  expirationLabel = `${hoursUntilExpiration}hrs left`;
                }
              } else if (daysUntilExpiration === 1) {
                expirationLabel = '1d left';
              } else {
                expirationLabel = `${daysUntilExpiration}d left`;
              }
            }

            return (
              <Stack spacing={0.5} alignItems="flex-start">
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    color: theme.palette.success.dark,
                    fontWeight: theme.typography.fontWeightMedium,
                    fontSize: theme.typography.caption.fontSize,
                  }}
                />
                {expirationLabel && (
                  <Chip
                    label={expirationLabel}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                      color: theme.palette.warning.dark,
                      fontWeight: theme.typography.fontWeightMedium,
                      fontSize: theme.typography.caption.fontSize,
                    }}
                  />
                )}
              </Stack>
            );
          }

          return (
            <Chip
              label="Scheduled"
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.info.main, 0.15),
                color: theme.palette.info.dark,
                fontWeight: theme.typography.fontWeightMedium,
                fontSize: theme.typography.caption.fontSize,
              }}
            />
          );
        },
      },
      {
        field: 'pinAction',
        headerName: '',
        width: 50,
        minWidth: 50,
        editable: false,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: ({ row }: GridRenderCellParams<CustomRule>) => {
          const isPinned = isRowPinned(row.id);

          if (!isPinned) {
            return null;
          }

          return (
            <Tooltip title="Click to unpin row">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnpinRow(row.id);
                }}
                sx={{
                  color: theme.palette.warning.main,
                  '&:hover': {
                    color: theme.palette.warning.dark,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                  },
                }}
              >
                <PinIcon sx={{ fontSize: theme.typography.body2.fontSize }} />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ];

    // Only columns the server can sort by are sortable. `status` is sortable too:
    // its header click clears the explicit sort, falling back to status grouping.
    // Every other column is non-sortable to avoid offering sorts the server ignores.
    const SORTABLE_COLUMN_FIELDS = new Set<string>(['name', 'createdAt', 'status']);
    const withSortability = baseColumns.map((col) => ({
      ...col,
      sortable: SORTABLE_COLUMN_FIELDS.has(col.field),
    }));
    return withSortability as Array<GridColDef<CustomRule>>;
  }, [theme, isRowPinned, handleUnpinRow, newlyCreatedRules, showTooltips]);

  const [pinnedColumns, setPinnedColumns] = useState<{
    left?: Array<string>;
    right?: Array<string>;
  }>({
    left: ['startDate'],
    right: ['status', 'pinAction'],
  });

  return (
    <Box sx={{ p: 1 }}>
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1.25,
          mb: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                lineHeight: 1.2,
                background: `linear-gradient(135deg, ${themeColor}, ${alpha(themeColor, 0.7)})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Custom Rules
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Manage loyalty point rules and configurations
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() => setIsPreferencesDrawerOpen(true)}
              sx={{
                color: themeColor,
                '&:hover': {
                  bgcolor: alpha(themeColor, 0.1),
                },
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
            <Button
              variant="contained"
              size="small"
              onClick={() => setIsCreateDialogOpen(true)}
              sx={{
                bgcolor: themeColor,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: alpha(themeColor, 0.8),
                },
              }}
            >
              Create New Rule
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Search Filters */}
      <SearchFiltersSection
        themeColor={themeColor}
        customRuleIdInput={customRuleIdInput}
        nameInput={nameInput}
        offerIdInput={offerIdInput}
        packageIdInput={packageIdInput}
        ratePlanIdInput={ratePlanIdInput}
        regionInput={regionInput}
        earnKeyInput={earnKeyInput}
        promoKeyInput={promoKeyInput}
        selectedProductType={selectedProductType}
        selectedType={selectedType}
        selectedPointsType={selectedPointsType}
        startDateFrom={startDateFrom}
        endDateTo={endDateTo}
        setCustomRuleIdInput={setCustomRuleIdInput}
        setNameInput={setNameInput}
        setOfferIdInput={setOfferIdInput}
        setPackageIdInput={setPackageIdInput}
        setRatePlanIdInput={setRatePlanIdInput}
        setRegionInput={setRegionInput}
        setEarnKeyInput={setEarnKeyInput}
        setPromoKeyInput={setPromoKeyInput}
        setSelectedProductType={setSelectedProductType}
        setSelectedType={setSelectedType}
        setSelectedPointsType={setSelectedPointsType}
        setStartDateFrom={setStartDateFrom}
        setEndDateTo={setEndDateTo}
        onSearch={handleSearch}
        onClear={handleClearFilters}
        validationErrors={validationErrors}
      />

      {/* Status Filter */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: theme.typography.fontWeightBold }}>
            Status Filter:
          </Typography>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_e, newValue) => {
              if (newValue !== null) {
                setStatusFilter(newValue as typeof statusFilter);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                '&.Mui-selected': {
                  bgcolor: alpha(themeColor, 0.15),
                  color: themeColor,
                  fontWeight: theme.typography.fontWeightBold,
                  '&:hover': {
                    bgcolor: alpha(themeColor, 0.25),
                  },
                },
                '&:hover': {
                  bgcolor: alpha(themeColor, 0.05),
                },
              },
            }}
          >
            <ToggleButton value="all">All ({stats.total})</ToggleButton>
            <ToggleButton value="active">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ActiveIcon sx={{ fontSize: theme.typography.body2.fontSize, color: theme.palette.success.main }} />
                <span>Active ({stats.active})</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="scheduled">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ScheduledIcon sx={{ fontSize: theme.typography.body2.fontSize, color: theme.palette.info.main }} />
                <span>Scheduled ({stats.scheduled})</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="expired">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ExpiredIcon sx={{ fontSize: theme.typography.body2.fontSize, color: theme.palette.grey[600] }} />
                <span>Expired ({stats.expired})</span>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* Row Pinning Controls */}
      <RowPinningSection
        selectedRowCount={rowSelectionModel.length}
        pinnedTopCount={pinnedRows.top.length}
        pinnedBottomCount={pinnedRows.bottom.length}
        onPinToTop={handlePinToTop}
        onPinToBottom={handlePinToBottom}
        onUnpinAll={handleUnpinAll}
        themeColor={themeColor}
      />

      {/* Data Grid */}
      <Paper elevation={0}>
        <DataGridPro
          // Grow to fit the current page's rows so the page scrolls as one unit
          // instead of a fixed-height inner scroll region.
          autoHeight
          key={`${pinnedRows.top.length}-${pinnedRows.bottom.length}-${paginationModel.page}`}
          apiRef={apiRef}
          rows={unpinnedRows}
          columns={columns}
          loading={isLoading}
          // Pinned rows (sticky rows that stay visible)
          pinnedRows={gridPinnedRows}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          // Server-side sorting driven by column headers (createdAt, name, status).
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          isRowSelectable={(params) => {
            // Disable checkbox for pinned rows
            return !isRowPinned(params.id as string);
          }}
          // Column visibility
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          // Column pinning
          pinnedColumns={pinnedColumns}
          onPinnedColumnsChange={setPinnedColumns}
          // Inline editing
          editMode="cell"
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          // Custom toolbar (without header filters)
          slots={{
            toolbar: CustomToolbar,
          }}
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row:hover': {
              bgcolor: alpha(themeColor, 0.08),
              zIndex: 1000,
            },
            '& .MuiDataGrid-row': {
              position: 'relative',
            },
            '& .MuiDataGrid-pinnedRows--top': {
              borderBottom: `3px solid ${alpha(themeColor, 0.3)}`,
              bgcolor: theme.palette.background.paper,
            },
            '& .MuiDataGrid-pinnedRows--bottom': {
              borderTop: `3px solid ${alpha(themeColor, 0.3)}`,
              bgcolor: theme.palette.background.paper,
            },
          }}
          // Keep row selection across pagination
          keepNonExistentRowsSelected
          density="comfortable"
        />
      </Paper>

      {/* Dialogs */}
      <CreateCustomRuleDialog
        themeColor={themeColor}
        open={isCreateDialogOpen}
        onClose={(newRule?: CustomRule) => {
          setIsCreateDialogOpen(false);
          if (newRule) {
            setNewlyCreatedRules((prev) => new Map(prev).set(newRule.id, Date.now()));

            // Pin it immediately using the rule object we already have
            setPinnedRows((prev) => ({
              ...prev,
              top: [newRule, ...prev.top.filter((r) => r.id !== newRule.id)],
            }));
            enqueueSnackbar('New rule pinned to top for easy access', { variant: 'info' });
          }
        }}
      />
      <UpdateCustomRuleDialog
        themeColor={themeColor}
        open={isUpdateDialogOpen}
        onClose={handleUpdateDialogClose}
        rule={selectedRule}
      />
      <ReadOnlyCustomRuleDialog
        themeColor={themeColor}
        open={isReadOnlyDialogOpen}
        onClose={handleReadOnlyDialogClose}
        rule={selectedRule}
      />

      {/* Column preferences modal — opened from page settings */}
      <ColumnPreferencesModal
        open={isPreferencesModalOpen}
        onClose={() => setIsPreferencesModalOpen(false)}
        onSave={handleSaveColumnPreferences}
        availableColumns={columns
          .filter(
            (col) =>
              col.field !== '__check__' &&
              col.field !== '__reorder__' &&
              col.field !== 'actions' &&
              col.field !== 'pinAction' &&
              col.field !== 'details',
          )
          .map((col) => ({ field: col.field, headerName: col.headerName || col.field }))}
      />

      {/* Preferences drawer */}
      <PreferencesDrawer
        open={isPreferencesDrawerOpen}
        onClose={() => setIsPreferencesDrawerOpen(false)}
        onChooseColumns={() => setIsPreferencesModalOpen(true)}
        onResetColumns={() => {
          localStorage.removeItem(COLUMN_PREFERENCES_STORAGE_KEY);
          setColumnVisibilityModel(DEFAULT_COLUMN_VISIBILITY);
          enqueueSnackbar('Column preferences reset to defaults.', { variant: 'info' });
        }}
        themeColor={themeColor}
        onThemeColorChange={handleThemeColorChange}
        showTooltips={showTooltips}
        onTooltipToggle={handleTooltipToggle}
      />
    </Box>
  );
}
